// TagChipInput — 태그 입력·자동완성·칩 목록 통합 컴포넌트 (제어형, 태그 이름 기준).
//
// 2026-10-03 개편: 이전엔 item id + 태그 id로 직접 추가/삭제 API를 호출했는데, 상세 패널이
//   태그 이름만 갖고 있어 가짜 id(idx+1)로 삭제 요청 → 엉뚱한 태그가 지워질 수 있었다.
//   이제 값은 태그 이름 배열이고, 변경은 onChange(새 배열)로 상위에 위임한다.
//   - 상세 보기: onChange → PATCH tags(전체 교체) 즉시 저장
//   - 수정 폼: onChange → 폼 상태, 저장 버튼에서 함께 PATCH
// 동작:
//   - 1자 이상 입력 → 자동완성 드롭다운 (useTags), ↑↓·Enter·클릭으로 선택
//   - 새 태그명 + Enter → 추가 (서버가 없는 태그는 자동 생성)
//   - 칩 X → 제거 / 빈 입력에서 Backspace → 마지막 칩 제거

import { useEffect, useRef, useState } from 'react';
import { useTags } from '../hooks/useTags';
import { TagPill } from './TagPill';
import type { Tag } from '../api/tags';

// ─── Props ────────────────────────────────────────────────

export interface TagChipInputProps {
  /** 현재 태그 이름 목록 */
  value: string[];
  /**
   * 태그 목록 변경 — 새 배열 전달. Promise를 반환하면 처리 중 입력을 막는다.
   * 실패 처리(토스트 등)는 호출 측 책임.
   */
  onChange: (names: string[]) => void | Promise<void>;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** placeholder 텍스트 */
  placeholder?: string;
}

// ─── TagChipInput 컴포넌트 ────────────────────────────────

export function TagChipInput({
  value,
  onChange,
  disabled = false,
  placeholder = '태그 추가...',
}: TagChipInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  // 칩 표시용 Tag 객체 (색상은 TagPill이 이름 해시로 결정)
  const tags: Tag[] = value.map((name, idx) => ({ id: idx + 1, name, color: null }));

  // 자동완성 — 1자 이상 입력 시 fetch
  const { data: suggestions = [] } = useTags(
    inputValue.length >= 1 ? inputValue : '',
    20,
  );

  // 이미 붙은 태그는 자동완성에서 제외 (이름 기준, 대소문자 무시)
  const lowerSet = new Set(value.map((n) => n.toLowerCase()));
  const filteredSuggestions = suggestions.filter((s) => !lowerSet.has(s.name.toLowerCase()));

  // 드롭다운 열림 조건: 입력 1자 이상
  useEffect(() => {
    setIsOpen(inputValue.length >= 1);
    setHighlightIdx(-1);
  }, [inputValue]);

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  /** onChange 호출 — Promise면 끝날 때까지 pending */
  const commit = async (next: string[]) => {
    setPending(true);
    try {
      await onChange(next);
    } catch {
      // 실패 알림은 호출 측(onChange)이 담당
    } finally {
      setPending(false);
    }
  };

  // ─── 태그 추가 / 제거 ────────────────────────────────

  const handleAddTag = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setInputValue('');
    setIsOpen(false);
    // 중복 방지
    if (lowerSet.has(trimmed.toLowerCase())) return;
    await commit([...value, trimmed]);
  };

  const handleRemoveTag = async (tag: Tag) => {
    await commit(value.filter((n) => n !== tag.name));
  };

  // ─── 키보드 핸들러 ─────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) =>
        filteredSuggestions.length === 0 ? -1 : Math.min(prev + 1, filteredSuggestions.length - 1),
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && filteredSuggestions[highlightIdx]) {
        // 자동완성 선택
        handleAddTag(filteredSuggestions[highlightIdx].name);
      } else if (inputValue.trim()) {
        // 신규 태그 생성
        handleAddTag(inputValue);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIdx(-1);
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      // 빈 입력에서 Backspace → 마지막 칩 제거 (UX 보조)
      const lastTag = tags[tags.length - 1];
      handleRemoveTag(lastTag);
    }
  };

  // ─── 렌더 ──────────────────────────────────────────

  return (
    <div className="tag-chip-input">
      {/* 기존 태그 칩 목록 */}
      <div className="tag-chip-input__chips" aria-label="현재 태그 목록">
        {tags.map((tag) => (
          <TagPill
            key={tag.name}
            tag={tag}
            onRemove={!disabled && !pending ? handleRemoveTag : undefined}
            removing={pending}
            size="sm"
          />
        ))}

        {/* 입력창 */}
        <div className="tag-chip-input__field-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="tag-chip-input__input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length >= 1 && setIsOpen(true)}
            placeholder={tags.length === 0 ? placeholder : ''}
            disabled={disabled || pending}
            aria-label="태그 입력"
            aria-autocomplete="list"
            aria-expanded={isOpen && filteredSuggestions.length > 0}
            aria-controls="tag-suggestions"
            autoComplete="off"
          />

          {/* 자동완성 드롭다운 */}
          {isOpen && filteredSuggestions.length > 0 && (
            <ul
              id="tag-suggestions"
              ref={dropdownRef}
              className="tag-chip-input__dropdown"
              role="listbox"
              aria-label="태그 자동완성 목록"
            >
              {filteredSuggestions.map((suggestion, idx) => (
                <li
                  key={suggestion.id}
                  role="option"
                  aria-selected={highlightIdx === idx}
                  className={`tag-chip-input__suggestion${highlightIdx === idx ? ' tag-chip-input__suggestion--highlighted' : ''}`}
                  onMouseDown={(e) => {
                    // blur 이전 선택이 되도록 mousedown에서 처리
                    e.preventDefault();
                    handleAddTag(suggestion.name);
                  }}
                  onMouseEnter={() => setHighlightIdx(idx)}
                >
                  {/* 색상 점 */}
                  <span
                    className="tag-pill__dot"
                    style={{
                      backgroundColor:
                        suggestion.color ??
                        (() => {
                          // 인라인 폴백 (TagPill hashColor와 동일 로직)
                          const FALLBACK = [
                            '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
                            '#f97316', '#eab308', '#22c55e', '#14b8a6',
                            '#3b82f6', '#06b6d4',
                          ];
                          let h = 0;
                          for (let i = 0; i < suggestion.name.length; i++) {
                            h = (h * 31 + suggestion.name.charCodeAt(i)) & 0xffff;
                          }
                          return FALLBACK[h % FALLBACK.length];
                        })(),
                    }}
                    aria-hidden="true"
                  />
                  <span className="tag-chip-input__suggestion-name">{suggestion.name}</span>
                  {suggestion.item_count > 0 && (
                    <span className="tag-chip-input__suggestion-count muted">
                      {suggestion.item_count}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
